export interface RuntimeConfig {
  envName: string
  apiBaseUrl: string
  uploadBaseUrl: string
  downloadBaseUrl: string
  viewerBaseUrl: string
  requestTimeout: number
}

type RuntimeConfigInput = Partial<Record<keyof RuntimeConfig, unknown>>

const defaultConfig: RuntimeConfig = {
  envName: 'development',
  apiBaseUrl: '/api',
  uploadBaseUrl: '/api',
  downloadBaseUrl: '/api',
  viewerBaseUrl: '',
  requestTimeout: 300000
}

export class RuntimeConfigError extends Error {
  cause?: unknown

  constructor(message: string, cause?: unknown) {
    super(message)
    this.cause = cause
    this.name = 'RuntimeConfigError'
  }
}

export const runtimeConfigKey = Symbol('ivus-runtime-config')

function mergeDefined(...sources: RuntimeConfigInput[]) {
  return sources.reduce<Record<string, unknown>>((result, source) => {
    for (const [key, value] of Object.entries(source)) {
      if (value !== undefined) result[key] = value
    }
    return result
  }, {})
}

function requiredString(config: Record<string, unknown>, key: keyof RuntimeConfig) {
  const value = config[key]
  if (typeof value !== 'string' || !value.trim()) {
    throw new RuntimeConfigError(`运行时配置 ${key} 必须是非空字符串`)
  }
  return value.trim()
}

function normalizeBaseUrl(value: unknown, key: keyof RuntimeConfig, allowEmpty = false) {
  if (allowEmpty && (value === undefined || value === '')) return ''
  if (typeof value !== 'string' || !value.trim()) {
    throw new RuntimeConfigError(`运行时配置 ${key} 必须是有效的 URL 或同源绝对路径`)
  }

  const normalized = value.trim().replace(/\/+$/, '') || '/'
  const isSameOriginPath = normalized.startsWith('/') && !normalized.startsWith('//')
  let isHttpUrl = false
  try {
    const parsed = new URL(normalized)
    isHttpUrl = parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    isHttpUrl = false
  }

  if (!isSameOriginPath && !isHttpUrl) {
    throw new RuntimeConfigError(`运行时配置 ${key} 不是有效的 HTTP(S) URL 或同源绝对路径`)
  }
  return normalized
}

export function resolveRuntimeConfig(...sources: RuntimeConfigInput[]): RuntimeConfig {
  const merged = mergeDefined(defaultConfig, ...sources)
  const timeout = Number(merged.requestTimeout)
  if (!Number.isFinite(timeout) || timeout <= 0) {
    throw new RuntimeConfigError('运行时配置 requestTimeout 必须是大于 0 的数字')
  }

  return Object.freeze({
    envName: requiredString(merged, 'envName'),
    apiBaseUrl: normalizeBaseUrl(merged.apiBaseUrl, 'apiBaseUrl'),
    uploadBaseUrl: normalizeBaseUrl(merged.uploadBaseUrl, 'uploadBaseUrl'),
    downloadBaseUrl: normalizeBaseUrl(merged.downloadBaseUrl, 'downloadBaseUrl'),
    viewerBaseUrl: normalizeBaseUrl(merged.viewerBaseUrl, 'viewerBaseUrl', true),
    requestTimeout: timeout
  })
}

function readBuildConfig(): RuntimeConfigInput {
  return {
    envName: import.meta.env.VITE_ENV_NAME,
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
    uploadBaseUrl: import.meta.env.VITE_UPLOAD_BASE_URL,
    downloadBaseUrl: import.meta.env.VITE_DOWNLOAD_BASE_URL,
    viewerBaseUrl: import.meta.env.VITE_VIEWER_BASE_URL,
    requestTimeout: import.meta.env.VITE_REQUEST_TIMEOUT
  }
}

function readInlineConfig(): RuntimeConfigInput {
  if (typeof window === 'undefined') return {}
  return window.__IVUS_RUNTIME_CONFIG__ || {}
}

async function loadExternalConfig(): Promise<RuntimeConfigInput> {
  let response: Response
  try {
    response = await fetch('/config/runtime-config.json', { cache: 'no-store' })
  } catch (error) {
    throw new RuntimeConfigError('无法加载运行时配置 /config/runtime-config.json', error)
  }

  if (!response.ok) {
    throw new RuntimeConfigError(`运行时配置加载失败：HTTP ${response.status}`)
  }

  try {
    const value: unknown = await response.json()
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new RuntimeConfigError('运行时配置必须是 JSON 对象')
    }
    return value as RuntimeConfigInput
  } catch (error) {
    if (error instanceof RuntimeConfigError) throw error
    throw new RuntimeConfigError('运行时配置不是有效的 JSON', error)
  }
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const external = await loadExternalConfig()
  const config = resolveRuntimeConfig(readBuildConfig(), external, readInlineConfig())
  if (typeof window !== 'undefined') window.__IVUS_RUNTIME_CONFIG__ = config
  return config
}
